import { DragOverlay, Modifier, useDndMonitor } from "@dnd-kit/core";
import { useDndSortContext } from "./DndSortContext";
import styles from "../../Dnd/Dnd.module.scss";
import cx from "classnames";
import { useLayoutEffect, useRef, useState } from "react";
import { calculateEventValidity } from "./utils";
/**
 * Component for rendering previews as items are dragged.
 * The preview is determined by the rendering function stored in context.
 */
export default function DndSortOverlay() {
  const [valid, setValid] = useState(true);
  const { previewRenderer } = useDndSortContext();
  const ref = useRef<HTMLDivElement>(null);
  const [offsetState, setOffsetState] = useState<
    { top: number; left: number } | undefined
  >();

  useDndMonitor({
    onDragOver(event) {
      setValid(calculateEventValidity(event));
    },
  });

  const classes = cx(styles["drag-preview"], {
    [styles["can-be-dropped"]]: valid,
  });

  const preview = previewRenderer({ valid });

  // check if overlay has a dialog ancestor and if so, offset the overlay by the dialog ancestor's position
  useLayoutEffect(() => {
    const dialogAncestor = resolveDialogAncestor(ref.current);
    if (dialogAncestor) {
      const offset = {
        top: dialogAncestor.getBoundingClientRect().top ?? 0,
        left: dialogAncestor.getBoundingClientRect().left ?? 0,
      };
      if (
        offset.top !== offsetState?.top ||
        offset.left !== offsetState?.left
      ) {
        setOffsetState(offset);
      }
    } else if (!offsetState) {
      setOffsetState({ top: 0, left: 0 });
    }
  }, [preview, offsetState]);

  return (
    <div ref={ref}>
      {preview && offsetState ? (
        <DragOverlay
          className={classes}
          modifiers={[offsetPosition(offsetState)]}
          wrapperElement="form"
        >
          {preview}
        </DragOverlay>
      ) : null}
    </div>
  );
}

function resolveDialogAncestor(el: Element | null): HTMLDialogElement | null {
  if (!el) return null;

  // closest() searches ancestors within the same root (document or shadow)
  const dialog = el.closest("dialog");
  if (dialog) return dialog;

  // if we're inside a shadow root, cross the shadow boundary and keep searching
  const rootNode = el.getRootNode();
  if (rootNode instanceof ShadowRoot) {
    return resolveDialogAncestor(rootNode.host);
  }

  return null;
}

function offsetPosition({ top, left }: { top: number; left: number }) {
  return function (args: Parameters<Modifier>[0]) {
    const { transform } = args;

    return {
      ...transform,
      x: transform.x - left,
      y: transform.y - top,
    };
  };
}
